import React from "react";
import { Link, useParams } from "react-router-dom";
import { useResolvedOrgContext } from "../context/ResolvedOrgContext";
import { getOrgHomePath } from "../utils/canonicalPublicRoutes";

export default function OrganizationNotFound({ orgSlug: orgSlugProp }) {
  const params = useParams();
  const orgSlug = orgSlugProp || params.orgSlug || "";
  // When this page renders inside an organization's site (a shell catch-all
  // for a bad path), "home" must mean that organization's home — the visitor
  // never chose to leave the org. Call-site contract: sites that VERIFIED the
  // org is missing pass an explicit `orgSlug` prop (platform CTA is correct
  // there); the route catch-alls pass nothing, so org context comes from the
  // resolved-org provider (custom domains) or the :orgSlug route param.
  const resolvedOrg = useResolvedOrgContext();
  const resolvedOrgSlug = String(resolvedOrg?.orgSlug || "").trim();
  const routeOrgSlug = orgSlugProp ? "" : String(params.orgSlug || "").trim();
  const contextOrgSlug = resolvedOrgSlug || routeOrgSlug;
  const orgHomePath = contextOrgSlug ? getOrgHomePath(contextOrgSlug) : "";

  return (
    <main className="min-h-screen bg-[#f7f8fb] px-5 py-16 text-slate-950">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.10)]">
        <div className="h-2 bg-[linear-gradient(90deg,#0F4AA0,#16A34A,#D4A021)]" />
        <div className="p-7 sm:p-10">
          <div className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#0F4AA0]">
            Organization not found
          </div>
          <h1 className="mt-4 text-3xl font-black leading-tight sm:text-5xl">
            {orgHomePath ? "We could not find that page." : "We could not find this Lightly site."}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-8 text-slate-600">
            {orgHomePath
              ? "The link may be out of date. Head back to the homepage to keep exploring."
              : orgSlug
              ? `No organization is published at "${orgSlug}". Check the link or search for the organization from Lightly.`
              : "Check the link or search for the organization from Lightly."}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            {orgHomePath ? (
              <Link
                to={orgHomePath}
                className="inline-flex items-center justify-center rounded-2xl bg-[#0F4AA0] px-5 py-3 text-sm font-extrabold text-white no-underline"
              >
                Go to homepage
              </Link>
            ) : (
              <>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-2xl bg-[#0F4AA0] px-5 py-3 text-sm font-extrabold text-white no-underline"
                >
                  Go to Lightly
                </Link>
                <Link
                  to="/donate"
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-extrabold text-slate-800 no-underline"
                >
                  Find an organization
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
